/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { noop } from 'lodash/fp';
import * as challengeActions from 'app/actions/challengeActions';
import { GOLF } from 'app/constants/challengeOneSportIds';
import Grid from 'app/components/layout/Grid';
import GridCol from 'app/components/layout/GridCol';
import Modal from 'app/components/commons/Modal';
import Responsive from 'app/components/responsive/Responsive';
import gamesSelector from 'app/selectors/gamesSelector';
import positionsSelector from 'app/selectors/positionsSelector';
import entitiesSelector from 'app/selectors/entitiesSelector';
import { COMPLETED } from 'app/constants/challengeStatusTypes';
import {
  LIVE_CHALLENGE_POLL_DURATION,
  OPEN_CHALLENGE_POLL_DURATION
} from 'app/constants/pollDurations';
import { PLAYERBOARD, LEADERBOARD } from '../../constants/liveChallengeTabTypes';
import SimpleSticky from '../commons/SimpleSticky';
import ChallengeHead from './ChallengeHead';
import ChallengeNavBarMobile from './ChallengeNavBarMobile';
import ChallengeTitleMobile from './ChallengeTitleMobile';
import LeaderBoard from './LeaderBoard';
import LiveChallengeTabs from './LiveChallengeTabs';
import PlayerBoard from './PlayerBoard';
import TeamPerformance from './TeamPerformance';
import { enableChallengePolling, isInProgress } from '../../utils/challengeUtils';

const DESKTOP_HEADER_HEIGHT = 80;
const MOBILE_HEADER_HEIGHT = 50;

type Props = {
  displayWidget: boolean,
  enablePolling: boolean,
  gameFilter?: string,
  games: Array<Object>,
  hasProjections: boolean,
  isGolf: boolean,
  isLoadingOpponent: boolean,
  isRacing: boolean,
  name: string,
  onGameSelect: () => void,
  onPositionSelect: () => void,
  onResetOpponentEntry: () => void,
  opponentEntryId?: string,
  playerBoardTableInfo?: Object,
  players: Array<Object>,
  pollTimestamp: number,
  positionFilter?: string,
  positions: Array<Object>,
  searchTerm?: string,
  setPollDuration: (duration: number) => void,
  showAllPositionsFilter: boolean,
  showProjectedScore: boolean,
  startChallengePolling: () => void,
  statusId: number,
  stopChallengePolling: () => void,
  userHasEntered: boolean
};

type State = {
  widgetPlayerId?: string,
  currentNavBarItem: string
};

class LiveChallenge extends React.Component<Props, State> {
  props: Props;

  state: State = {
    widgetPlayerId: undefined,
    currentNavBarItem: LEADERBOARD
  };

  static defaultProps = {
    gameFilter: undefined,
    opponentEntryId: undefined,
    playerBoardTableInfo: undefined,
    positionFilter: undefined,
    searchTerm: undefined,
  };

  componentDidMount() {
    const { enablePolling, setPollDuration, startChallengePolling, statusId } = this.props;
    setPollDuration(LIVE_CHALLENGE_POLL_DURATION);

    if (enablePolling) {
      startChallengePolling();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { enablePolling, positionFilter, statusId, stopChallengePolling } = this.props;

    if (positionFilter !== nextProps.positionFilter) {
      this.setState({
        widgetPlayerId: undefined
      });
    }
    if (nextProps.statusId === COMPLETED && enablePolling) {
      stopChallengePolling();
    }
  }

  componentWillUnmount() {
    const { setPollDuration, onResetOpponentEntry, stopChallengePolling } = this.props;

    stopChallengePolling();
    onResetOpponentEntry();
    setPollDuration(OPEN_CHALLENGE_POLL_DURATION);
  }

  closeWidget = () => {
    this.setState({
      widgetPlayerId: undefined
    });
  };

  openWidget = (id: number) => {
    this.setState({
      widgetPlayerId: id
    });
  };

  toggleWidget = (id: number) => {
    this.setState(state => ({
      widgetPlayerId: state.widgetPlayerId === id ? undefined : id
    }));
  };

  updateNavBar = (newNavBarItem: ChallengeNavItem) => {
    this.setState({
      currentNavBarItem: newNavBarItem
    });
  };

  render() {
    const {
      displayWidget,
      gameFilter,
      games,
      hasProjections,
      isGolf,
      isLoadingOpponent,
      isRacing,
      name,
      onGameSelect,
      onPositionSelect,
      opponentEntryId,
      playerBoardTableInfo,
      players,
      pollTimestamp,
      positionFilter,
      positions,
      searchTerm,
      showProjectedScore,
      showAllPositionsFilter,
      statusId,
      userHasEntered
    } = this.props;
    const { widgetPlayerId, currentNavBarItem } = this.state;
    const hideBoard = currentNavBarItem !== LEADERBOARD && currentNavBarItem !== PLAYERBOARD;
    const displayPlayersCount = isInProgress(statusId) && (games.length > 1 || isGolf);

    return (
      <Responsive desktop>
        {isDesktop => (
          <div>
            {!isDesktop && (
              <SimpleSticky>
                <ChallengeTitleMobile
                  displayTimer={isInProgress(statusId)}
                  pollTimestamp={pollTimestamp}
                >
                  {name}
                </ChallengeTitleMobile>
                <ChallengeNavBarMobile
                  activeNavItem={currentNavBarItem}
                  onSelect={this.updateNavBar}
                  isRacing={isRacing}
                />
              </SimpleSticky>
            )}
            <ChallengeHead showChallengeOverview={currentNavBarItem === 'overview'} />
            <Grid>
              {!hideBoard && (
                <GridCol
                  width={
                    userHasEntered || opponentEntryId || isLoadingOpponent ? 'three-fifths' : null
                  }
                >
                  <div>
                    {isDesktop && (
                      <LiveChallengeTabs
                        isInProgress={isInProgress(statusId)}
                        isRacing={isRacing}
                        onClick={this.updateNavBar}
                        pollTimestamp={pollTimestamp}
                        selected={currentNavBarItem}
                      />
                    )}
                    {currentNavBarItem === LEADERBOARD ? (
                      <LeaderBoard
                        displayPlayersCount={displayPlayersCount}
                        hasProjections={hasProjections}
                        isRacing={isRacing}
                        isSticky
                        offsetTop={isDesktop ? DESKTOP_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT}
                        players={players}
                        pollTimestamp={pollTimestamp}
                      />
                    ) : (
                      <PlayerBoard
                        displayWidget={displayWidget}
                        gameFilter={gameFilter}
                        games={games}
                        hasSearch
                        isRacing={isRacing}
                        isSticky
                        offsetTop={isDesktop ? DESKTOP_HEADER_HEIGHT : MOBILE_HEADER_HEIGHT}
                        onGameSelect={onGameSelect}
                        onPositionSelect={onPositionSelect}
                        onWidgetClick={this.toggleWidget}
                        playerBoardTableInfo={playerBoardTableInfo}
                        players={players}
                        positionFilter={positionFilter}
                        positions={positions}
                        searchTerm={searchTerm}
                        showAllPositionsFilter={showAllPositionsFilter}
                        showProjectedScore={showProjectedScore}
                        widgetPlayerId={widgetPlayerId}
                      />
                    )}
                  </div>
                </GridCol>
              )}
              {isDesktop && (userHasEntered || opponentEntryId || isLoadingOpponent) && (
                <GridCol width="two-fifths">
                  <TeamPerformance isSticky hasProjections={hasProjections} />
                </GridCol>
              )}
              {!isDesktop && (opponentEntryId || isLoadingOpponent) && (
                <Modal isOpen onCancel={noop} bare>
                  <TeamPerformance isModal hasProjections={hasProjections} />
                </Modal>
              )}
            </Grid>
          </div>
        )}
      </Responsive>
    );
  }
}

const connector = connect(
  state => ({
    challengeId: state.challenge.id,
    displayWidget: state.settings.displayWidget,
    gameFilter: state.lineUp.gameFilter,
    games: state.challenge.isRacing ? gamesSelector(state) : state.challenge.games,
    hasProjections: state.challenge.hasProjections,
    isGolf: state.challenge.onesportSportId === GOLF,
    isLoadingOpponent: state.entry.isLoadingOpponent,
    isRacing: state.challenge.isRacing,
    opponentEntryId: state.entry.opponentEntry.id,
    name: state.challenge.name,
    performanceDiffValue: state.entry.restrictToUnique,
    playerBoardTableInfo: state.challenge.playerBoardTableInfo,
    players: entitiesSelector(state),
    pollTimestamp: state.challenge.pollTimestamp,
    positionFilter: state.lineUp.positionFilter,
    positions: positionsSelector(state),
    requiredSelections: state.challenge.playerQuota + state.challenge.teamQuota,
    searchTerm: state.playerList.searchTerm,
    showAllPositionsFilter: state.lineUp.showAllPositionsFilter,
    showProjectedScore: state.playerList.showProjectedScore,
    statusId: state.challenge.statusId,
    userHasEntered: state.challenge.hasEntered,
    enablePolling: enableChallengePolling(state.challenge.statusId)
  }),
  {
    onGameSelect: challengeActions.setGameFilter,
    onPositionSelect: challengeActions.setPositionFilter,
    onResetOpponentEntry: challengeActions.resetOpponentEntry,
    setPollDuration: challengeActions.setPollDuration,
    startChallengePolling: challengeActions.pollChallenge,
    stopChallengePolling: challengeActions.stopPollingChallenge
  }
);

export default connector(LiveChallenge);
